import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { store } from "../redux/store";
import { showLoader, hideLoader } from "../redux/slice/loaderSlice";
import { logout } from "../redux/slice/userSlice";

// Create axios instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL + "api/",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 100000,
  withCredentials: true,
});

// 🔁 Refresh handling variables
let isRefreshing = false;
let failedQueue: any[] = [];

// Process queued requests
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ✅ REQUEST INTERCEPTOR
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    store.dispatch(showLoader());

    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    store.dispatch(hideLoader());
    return Promise.reject(error);
  }
);

// ✅ RESPONSE INTERCEPTOR (MAIN LOGIC)
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    store.dispatch(hideLoader());
    return response;
  },
  async (error: AxiosError) => {
    store.dispatch(hideLoader());

    const originalRequest: any = error.config;

    // 🔥 HANDLE TOKEN EXPIRE
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          throw new Error("No refresh token");
        }

        const res = await axios.post(
          process.env.REACT_APP_API_URL + "api/refresh-token",
          { refreshToken }
        );

        const newToken = res.data.token;

        // ✅ Save new token
        localStorage.setItem("authToken", newToken);

        // ✅ Update default header
        apiClient.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newToken}`;

        processQueue(null, newToken);

        // ✅ Retry original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);

      } catch (err) {
        processQueue(err, null);

        // ❌ Logout ONLY if refresh fails
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");

        store.dispatch(logout());
        window.location.href = "/login";

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ✅ API REQUEST FUNCTION
interface ApiRequestProps {
  method: string;
  url: string;
  data?: any;
  params?: any;
  headers?: Record<string, string>;
  config?: AxiosRequestConfig;
}

export const apiRequest = async ({
  method = "get",
  url,
  data = null,
  params = null,
  headers = {},
  config = {},
}: ApiRequestProps): Promise<any> => {
  try {
    const response = await apiClient({
      method,
      url,
      data,
      params,
      headers,
      ...config,
    });

    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Something went wrong";

    console.error("API Error:", error.response);
    throw new Error(errorMessage);
  }
};

export default apiClient;